import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
	ConstantColors,
	LocationHistory,
	Point,
	SegmentHistory,
} from "../redux/reducers/Archives/GPSLog/interface";
import MapGPSBase from "../components/MapGPS";
import { ConstantFormat } from "../utils/DateFns";
import { ExecutorStatus } from "../services/Executor";
import { useAppContext } from "../context";
import { ConstantLanguage } from "../assets/languages/langs";
import { upperCaseFirst } from "../utils";

export interface UseExecutorTrackLayers {
	/** The polyline of the whole track */
	positions: Point[];
	/** These points indicate the starts and ends of the paths */
	trackStartEnd: MapGPSBase.TrackStartEnd[];
	/** Circle markers with descriptions of the speed, accuracy, altitude */
	circleMarkerItems: MapGPSBase.CircleMarkerItems[][];
}

/** `useExecutorTrackLayers`
 ** Converts the executor route of an order into the map layers
 ** of `MapGPS` (polyline, start/end markers, circle markers).
 */
export const useExecutorTrackLayers = (
	executorRoute: LocationHistory[],
	radius: number,
	closedAt?: string | number | Date | null,
): UseExecutorTrackLayers => {
	const { dateFns, language } = useAppContext();
	const { t } = useTranslation();

	const transText = useMemo(
		() => ({
			speed: t(`speed`),
			accuracy: t(`accuracy`),
			altitude: t(`altitude`),
			mpsUnit: t(`units.mps`),
			kphUnit: t(`units.kph`),
			meter: t(`units.meter`),

			startOrder: upperCaseFirst(t("mapGPS.str207")),
			orderStatus: `${upperCaseFirst(t(`status`))}: `,
			point: `${upperCaseFirst(t("point"))}: `,
			date: `${upperCaseFirst(t(`date`))}: `,
			durationMS: `${upperCaseFirst(t("mapGPS.str202"))}: `,
			registrationNumber: `${upperCaseFirst(t("mapGPS.str203"))}: `,
			carNumber: `${upperCaseFirst(t("mapGPS.str205"))}: `,
			executorCallSign: `${upperCaseFirst(t("mapGPS.str206"))}: `,
			executorStatus: `${upperCaseFirst(t("mapGPS.str208"))}: `,
			start: `${upperCaseFirst(t(`mapGPS.str200`))}: `,
			end: `${upperCaseFirst(t("mapGPS.str201"))}: `,
		}),
		[t],
	);
	const executorStatuses = useMemo(
		() => ({
			busy: t("orderPageWidgets.parking.parkingTable.str202") ?? "",
			dinner: t("orderPageWidgets.parking.parkingTable.str203") ?? "",
			home: t("orderPageWidgets.parking.parkingTable.str204") ?? "",
			on_order: t("orderPageWidgets.parking.parkingTable.str205") ?? "",
			available: t("orderPageWidgets.parking.parkingTable.str206") ?? "",
			closed: t("orderPageWidgets.parking.parkingTable.str207") ?? "",
			ownOrder: t("orderPageWidgets.parking.parkingTable.str208") ?? "",
			debt: t("orderPageWidgets.executors.str206") ?? "",
		}),
		[t],
	);

	const getStatus = useCallback(
		(status) => {
			if (!status) return "";

			if (status === ExecutorStatus.BUSY) {
				return executorStatuses.busy;
			}

			if (status === ExecutorStatus.DINNER) {
				return executorStatuses.dinner;
			}

			if (status === ExecutorStatus.HOME) {
				return executorStatuses.home;
			}

			if (status === ExecutorStatus.ON_ORDER) {
				return executorStatuses.on_order;
			}

			return executorStatuses[status] || status;
		},
		[executorStatuses],
	);

	const formatTime = useMemo(
		() => ({
			full:
				language === ConstantLanguage.EN
					? ConstantFormat.MDYHMS
					: ConstantFormat.MDYHMS_UK,
			minutes:
				language === ConstantLanguage.EN
					? ConstantFormat.HMS
					: ConstantFormat.HMS_UK,
			day:
				language === ConstantLanguage.EN
					? ConstantFormat.MDY
					: ConstantFormat.MDY_UK,
		}),
		[language],
	);

	const converterLine = useCallback(
		(data: SegmentHistory, start: SegmentHistory): [string, string][] => {
			const mps = `${data.speed?.toFixed(2) || 0} ${transText.mpsUnit}`;
			const kph = `${(data.speed * 3.6)?.toFixed(2) || 0} ${
				transText.kphUnit
			}`;

			const startTime = start.date;
			const endTime = data.date;

			const durationMS = dateFns.formatDistance(
				endTime,
				startTime,
				language,
			);

			return [
				[
					transText.date ?? "",
					dateFns.formatTime2(new Date(data.date), formatTime.full, {
						lang: language,
						timezone: dateFns.timezone,
					}),
				],
				[
					transText.point,
					dateFns.formatTime2(
						new Date(data.date),
						formatTime.minutes,
						{
							lang: language,
							timezone: dateFns.timezone,
						},
					),
				],
				[
					transText.start ?? "",
					dateFns.formatTime2(
						new Date(start.date),
						formatTime.minutes,
						{
							lang: language,
							timezone: dateFns.timezone,
						},
					),
				],
				[
					transText.end ?? "",
					closedAt
						? dateFns.formatTime2(
								new Date(closedAt),
								formatTime.minutes,
								{
									lang: language,
									timezone: dateFns.timezone,
								},
						  )
						: "",
				],
				[
					transText.durationMS ?? "",
					data.date === start.date ? "" : durationMS,
				],
				[
					transText.registrationNumber ?? "",
					data?.car?.registrationNumber || "",
				],
				[transText.carNumber ?? "", data?.carNumber || ""],
				[transText.executorCallSign ?? "", data?.executorCallSign],
				[
					transText.executorStatus ?? "",
					getStatus(data.executor?.status),
				],
				[
					transText.orderStatus,
					data.order?.executingStage
						? t(
								`order_executing_stages.${data.order.executingStage}`,
						  ) || data.order?.executingStage
						: "",
				],
				[transText.speed, `${kph} (${mps})`],
				[
					transText.altitude,
					`${data.altitude?.toFixed(2) || 0} ${transText.meter}`,
				],
				[
					transText.accuracy,
					`${data.accuracy?.toFixed(2) || 0} ${transText.meter}`,
				],
			];
		},
		[closedAt, transText, dateFns, formatTime, getStatus, language, t],
	);

	const { trackStartEnd, positions } = useMemo<{
		positions: Point[];
		trackStartEnd: MapGPSBase.TrackStartEnd[];
	}>(() => {
		if (!executorRoute?.length) {
			return {
				positions: [],
				trackStartEnd: [],
			};
		}
		const startEnd: MapGPSBase.TrackStartEnd[] = [];
		const track: Point["points"] = [];
		executorRoute.forEach(({ segments }) =>
			segments.forEach((segment): void => {
				segment.forEach((seg) => {
					track.push(seg.point);
				});
				const start = segment.at(0);
				const end = segment.at(-1);
				// The end path
				if (start && end) {
					startEnd.push({
						lines: converterLine(end, start),
						point: end.point,
						startTimestamp: false,
						endTimestamp: true,
					});
				}
				if (start) {
					// The start path
					startEnd.push({
						lines: converterLine(start, start),
						point: start.point,
						startTimestamp: true,
						endTimestamp: false,
					});
				}
			}),
		);

		return {
			trackStartEnd: startEnd,
			positions: [
				{
					order: true,
					points: track,
				},
			],
		};
	}, [converterLine, executorRoute]);

	const circleMarkerItems = useMemo<MapGPSBase.CircleMarkerItems[][]>(() => {
		if (!executorRoute?.length) return [];

		return executorRoute
			.map(({ segments }, outerIndex) =>
				segments.map((segment, segmentIndex) =>
					segment.map((point, pointIndex) => {
						const start = segment.at(0);
						// Speed is stored in M/S
						const rad = radius === 0 ? 1 : radius * 10;

						return {
							keyPoint: `${outerIndex}-${segmentIndex}-${pointIndex}`,
							center: point.point,
							radius: rad,
							lines: converterLine(point, start || point),
							date: point.date,
							duration: 0,
							deg: 0,
							color: point?.order
								? ConstantColors.RED
								: ConstantColors.BLUE,
						};
					}),
				),
			)
			.flat();
	}, [converterLine, executorRoute, radius]);

	return useMemo(
		() => ({ positions, trackStartEnd, circleMarkerItems }),
		[positions, trackStartEnd, circleMarkerItems],
	);
};

export default useExecutorTrackLayers;
